'use client';

import { useState } from 'react';
import Link from 'next/link';
import PropTypes from 'prop-types';
import Icon from '@/components/ui/AppIcon';
import {
  DRAND,
  drandUrls,
  entriesHash,
  pickWinners,
  randomnessFromSignature,
  roundTime,
  shortHex,
} from '@/lib/draws/fairDraw';

const when = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'Europe/London',
      })
    : '';

/**
 * "How this draw is made": the public record of the competition's draw and
 * a button that checks it in the visitor's own browser, against drand's own
 * servers. `proof` is what /api/draws/<id> returns.
 */
export default function DrawProof({ competitionId, proof }) {
  const [run, setRun] = useState(null); // { steps: [{ label, ok, detail }], done }

  const commitment = proof?.commitment;
  const draw = proof?.draw;
  if (!commitment) return null;

  const drawn = Boolean(draw);
  // The round is named when the draw starts, so before that there is none.
  const named = commitment.beacon_round != null;
  const roundAt = !named
    ? null
    : commitment.beacon_time
      ? new Date(commitment.beacon_time)
      : roundTime(commitment.beacon_round);

  const verify = async () => {
    const steps = [];
    const push = (label, ok, detail = '') => {
      steps.push({ label, ok, detail });
      setRun({ steps: [...steps], done: false });
    };
    try {
      // 1. The entry list and its fingerprint.
      const res = await fetch(`/api/draws/${competitionId}/entries`, { cache: 'no-store' });
      if (!res.ok) throw new Error('The entry list could not be downloaded.');
      const tickets = (await res.text()).split(/\s+/).filter(Boolean).map(Number);
      const hash = await entriesHash(tickets);
      push(
        `Entry list: ${tickets.length.toLocaleString('en-GB')} tickets, fingerprint ${shortHex(hash)}`,
        hash === commitment.entries_hash && tickets.length === commitment.entries_count,
        hash === commitment.entries_hash
          ? 'Matches the fingerprint published when entries closed.'
          : 'Does NOT match the published fingerprint.'
      );

      // 2. The randomness, from drand directly.
      if (!named) {
        setRun({ steps, done: true });
        return;
      }
      let beacon = null;
      let source = '';
      for (const url of drandUrls(commitment.beacon_round)) {
        try {
          const r = await fetch(url, { cache: 'no-store' });
          if (r.ok) {
            beacon = await r.json();
            source = new URL(url).host;
            break;
          }
        } catch {
          // try the next mirror
        }
      }
      if (!beacon?.signature)
        throw new Error('drand could not be reached from your browser. Try again in a moment.');
      const randomness = await randomnessFromSignature(beacon.signature);
      push(
        `drand round ${Number(commitment.beacon_round).toLocaleString('en-GB')} from ${source}: randomness ${shortHex(randomness)}`,
        drawn ? randomness === draw.beacon_randomness : true,
        drawn
          ? randomness === draw.beacon_randomness
            ? 'Matches the randomness recorded for this draw.'
            : 'Does NOT match the randomness recorded for this draw.'
          : 'Published and ready; the draw has not been run yet.'
      );

      // 3. The arithmetic.
      if (drawn) {
        const wanted = Math.max(1, (draw.winning_numbers || []).length);
        const { winners } = await pickWinners({ randomness, competitionId, tickets, wanted });
        const recorded = (draw.winning_numbers || []).map(Number);
        const same =
          winners.length === recorded.length && winners.every((t, i) => t === recorded[i]);
        push(
          `Winner${winners.length > 1 ? 's' : ''} from the arithmetic: ticket ${winners.join(', ')}`,
          same,
          same ? 'Exactly the recorded result.' : `Recorded result was ${recorded.join(', ')}.`
        );
      }
      setRun({ steps, done: true });
    } catch (err) {
      push(err?.message || 'The check could not be completed.', false);
      setRun({ steps, done: true });
    }
  };

  return (
    <section
      className="rounded-soft border border-border bg-card/60 p-5"
      aria-labelledby="draw-proof-h"
    >
      <h3 id="draw-proof-h" className="text-base font-bold text-foreground">
        {drawn ? 'How this draw was made' : 'How the draw will be made'}
      </h3>
      <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="text-muted-foreground">Entries frozen</dt>
          <dd className="font-semibold text-foreground">
            {when(commitment.committed_at)} ·{' '}
            {Number(commitment.entries_count).toLocaleString('en-GB')} tickets
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Entry list fingerprint</dt>
          <dd className="font-mono text-xs text-foreground break-all">{commitment.entries_hash}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Random source</dt>
          <dd className="font-semibold text-foreground">
            {named ? (
              <>
                drand {DRAND.chain}, round {Number(commitment.beacon_round).toLocaleString('en-GB')}
                , named {when(commitment.beacon_named_at)}
                {commitment.beacon_latest_round_at_naming != null &&
                  ` (drand had published up to round ${Number(
                    commitment.beacon_latest_round_at_naming
                  ).toLocaleString('en-GB')})`}
                , published {when(roundAt.toISOString())}
              </>
            ) : (
              `drand ${DRAND.chain}: the round is named when the draw starts, a few seconds before drand publishes it`
            )}
          </dd>
        </div>
        {drawn && (
          <>
            <div>
              <dt className="text-muted-foreground">Randomness</dt>
              <dd className="font-mono text-xs text-foreground break-all">
                {draw.beacon_randomness}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Drawn</dt>
              <dd className="font-semibold text-foreground">
                {when(draw.drawn_at)} · winning ticket {(draw.winning_numbers || []).join(', ')}
              </dd>
            </div>
          </>
        )}
      </dl>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button type="button" onClick={verify} className="btn-block btn-go !py-2.5 !px-4 text-sm">
          <Icon name="ShieldCheckIcon" size={16} />
          {drawn ? 'Verify this draw' : 'Check the entry list'}
        </button>
        <a
          href={`/api/draws/${competitionId}/entries`}
          className="link-more text-sm"
          target="_blank"
          rel="noopener noreferrer"
        >
          Download the entry list
        </a>
        {named && (
          <a
            href={drandUrls(commitment.beacon_round)[0]}
            className="link-more text-sm"
            target="_blank"
            rel="noopener noreferrer"
          >
            See the round on drand
          </a>
        )}
        <Link href="/fair-draws" className="link-more text-sm">
          How it works
        </Link>
      </div>

      {run && (
        <ol className="mt-4 space-y-2 text-sm">
          {run.steps.map((s, i) => (
            <li key={i} className="flex items-start gap-2">
              <Icon
                name={s.ok ? 'CheckCircleIcon' : 'XCircleIcon'}
                size={18}
                className={`mt-0.5 shrink-0 ${s.ok ? 'text-accent-bright' : 'text-error'}`}
              />
              <div>
                <p className="font-semibold text-foreground">{s.label}</p>
                {s.detail && <p className="text-muted-foreground">{s.detail}</p>}
              </div>
            </li>
          ))}
          {run.done && run.steps.every((s) => s.ok) && (
            <li className="pt-1 font-semibold text-accent-bright">
              {drawn
                ? 'Everything checks out. This draw is exactly what the published facts produce.'
                : 'The entry list matches its published fingerprint.'}
            </li>
          )}
        </ol>
      )}
    </section>
  );
}

DrawProof.propTypes = {
  competitionId: PropTypes.string.isRequired,
  proof: PropTypes.shape({
    commitment: PropTypes.object,
    draw: PropTypes.object,
  }),
};
